import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { Toaster } from './components/Toaster';
import { LoadingRows } from './components/Feedback';
import { useAuth } from './state';
import { ApiKeyReveal, LoginPage } from './pages/LoginPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { TunnelsPage } from './pages/TunnelsPage';
import { SuitesPage } from './pages/SuitesPage';
import { SchedulesPage } from './pages/SchedulesPage';
import { SettingsPage } from './pages/SettingsPage';
import { BillingPage } from './pages/BillingPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminLlmPage } from './pages/AdminLlmPage';
import { AdminCustomersPage } from './pages/AdminCustomersPage';
import { AdminSmtpPage } from './pages/AdminSmtpPage';
import { LandingPage } from './pages/LandingPage';
import { GuidePage } from './pages/GuidePage';
import { LegalPage } from './pages/LegalPage';

interface IssuedKey {
  apiKey: string;
  scopes: readonly string[];
}

/**
 * One rule guards the app: no API key, no pages. The server enforces the same
 * thing; doing it here just means an unauthenticated tab lands on /login instead
 * of on a wall of 401s.
 *
 * The one-time key reveal deliberately sits above that rule. It cannot live
 * inside `LoginPage`: the instant a key exists the login route is unmounted, and
 * a component that unmounts cannot show a secret the server will never repeat.
 */
export function App(): JSX.Element {
  const { apiKey, ready } = useAuth();
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  if (!ready) {
    return (
      <div className="boot">
        <LoadingRows rows={2} label="Restoring your session" />
      </div>
    );
  }

  if (issued) {
    return (
      <>
        <Toaster />
        <ApiKeyReveal apiKey={issued.apiKey} scopes={issued.scopes} onDone={() => setIssued(null)} />
      </>
    );
  }

  return (
    <>
      <Toaster />
      {apiKey ? (
        <AppShell>
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/tunnels" element={<TunnelsPage />} />
            <Route path="/suites" element={<SuitesPage />} />
            <Route path="/schedules" element={<SchedulesPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/docs" element={<GuidePage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/llm" element={<AdminLlmPage />} />
            <Route path="/admin/customers" element={<AdminCustomersPage />} />
            <Route path="/admin/smtp" element={<AdminSmtpPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppShell>
      ) : (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/docs" element={<GuidePage />} />
          <Route path="/guide" element={<GuidePage />} />
          {/* Signup asks the user to agree to these, so the route has to exist
              before they have a key — the catch-all below would otherwise send
              the consent links back to the landing page. */}
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/login" element={<LoginPage onKeyIssued={setIssued} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="page">
      <h1>Nothing here</h1>
      <p className="muted">
        That route does not exist. <a href="/">Back to projects</a>.
      </p>
    </div>
  );
}